import React, { useEffect, useMemo, useState } from 'react';
import { courseService } from '../../services/courseService';
import { videoService } from '../../services/videoService';
import { chapterService } from '../../services/chapterService';
import { courseTaskService } from '../../services/courseTaskService';
import { Course, Video } from '../../types';
import { Chapter } from '../../services/chapterService';

const AdminUploadVideoPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | ''>('');
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [selectedChapterId, setSelectedChapterId] = useState<number | ''>('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoOrder, setVideoOrder] = useState<number>(0);
  const [videoPoints, setVideoPoints] = useState<number>(10); // Điểm kinh nghiệm khi xem xong video
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const run = async () => {
      const data = await courseService.getCourses(1, 100, undefined, 'all');
      setCourses(data.courses);
    };
    run();
  }, []);

  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedCourseId) {
        setChapters([]);
        setSelectedChapterId('');
        return;
      }
      try {
        const chaptersData = await chapterService.getCourseChapters(Number(selectedCourseId));
        setChapters(chaptersData);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };
    loadChapters();
  }, [selectedCourseId]);

  const canSubmitUpload = useMemo(() => {
    return !!selectedCourseId && !!videoFile && videoTitle.trim().length > 0;
  }, [selectedCourseId, videoFile, videoTitle]);

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setThumbnailFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setThumbnailPreview(null);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourseId || !videoFile) return;
    setUploading(true);
    try {
      const uploadedVideo = await videoService.uploadVideo(selectedCourseId, videoFile, {
        title: videoTitle,
        description: videoDescription,
        order: videoOrder,
        chapter_id: selectedChapterId ? Number(selectedChapterId) : undefined,
        thumbnailFile: thumbnailFile || undefined,
      } as Partial<Video> & { thumbnailFile?: File });
      
      // Tự động tạo task "video_watch" với điểm kinh nghiệm
      if (uploadedVideo && uploadedVideo.video && uploadedVideo.video.id) {
        try {
          const points = videoPoints > 0 ? videoPoints : 10; // Mặc định 10 điểm nếu không nhập hoặc = 0
          await courseTaskService.createCourseTask({
            title: `Xem video: ${videoTitle}`,
            description: `Hoàn thành video để nhận ${points} điểm kinh nghiệm`,
            points: points,
            task_type: 'video_watch',
            video_id: uploadedVideo.video.id,
            chapter_id: selectedChapterId ? Number(selectedChapterId) : undefined,
            order: 0,
            is_active: true,
          } as any);
        } catch (taskError: any) {
          // Nếu tạo task thất bại, vẫn hiển thị thành công upload video nhưng cảnh báo
          console.error('Không thể tạo task video_watch:', taskError);
          alert('Tải video thành công, nhưng không thể tạo task điểm kinh nghiệm. Vui lòng tạo thủ công trong trang quản lý khóa học.');
        }
      }
      
      setVideoTitle('');
      setVideoDescription('');
      setVideoOrder(0);
      setVideoPoints(10); // Reset về mặc định
      setVideoFile(null);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setSelectedChapterId('');
      alert('Tải video thành công và đã tự động gắn điểm kinh nghiệm!');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Tải video thất bại');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-xl border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium">Tải video khóa học</h2>
      </div>
      <form className="p-4 space-y-3" onSubmit={handleUpload}>
        <label className="block text-sm">
          <span className="text-gray-700">Chọn khóa học</span>
          <select
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            value={selectedCourseId}
            onChange={(e) => {
              setSelectedCourseId(e.target.value ? Number(e.target.value) : '');
              setSelectedChapterId('');
            }}
          >
            <option value="">-- Chọn khóa học --</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.id} - {c.title}
              </option>
            ))}
          </select>
        </label>
        {selectedCourseId && (
          <label className="block text-sm">
            <span className="text-gray-700">Chọn chương (tùy chọn)</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={selectedChapterId}
              onChange={(e) => setSelectedChapterId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">-- Không chọn chương --</option>
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block text-sm">
          <span className="text-gray-700">Tiêu đề video</span>
          <input
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            value={videoTitle}
            onChange={(e) => setVideoTitle(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Mô tả</span>
          <textarea
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            rows={3}
            value={videoDescription}
            onChange={(e) => setVideoDescription(e.target.value)}
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-gray-700">Thứ tự</span>
            <input
              type="number"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={videoOrder}
              onChange={(e) => setVideoOrder(Number(e.target.value))}
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">⭐ Điểm kinh nghiệm khi xem xong</span>
            <input
              type="number"
              className="mt-1 w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
              value={videoPoints}
              onChange={(e) => setVideoPoints(Number(e.target.value) || 10)}
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">Điểm tự động cấp khi học viên xem xong video (100%)</p>
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-gray-700">File video</span>
          <input
            type="file"
            accept="video/*"
            className="mt-1 w-full"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">Ảnh đại diện (Thumbnail)</span>
          <input
            type="file"
            accept="image/*"
            className="mt-1 w-full"
            onChange={handleThumbnailChange}
          />
          {thumbnailPreview && (
            <div className="mt-2">
              <img
                src={thumbnailPreview}
                alt="Thumbnail preview"
                className="max-w-xs max-h-48 object-contain border border-gray-300 rounded-lg"
              />
            </div>
          )}
        </label>
        <button
          type="submit"
          className="w-full py-2 rounded-lg bg-secondary-600 text-white disabled:opacity-50"
          disabled={!canSubmitUpload || uploading}
        >
          {uploading ? 'Đang tải...' : 'Tải video'}
        </button>
      </form>
    </div>
  );
};

export default AdminUploadVideoPage;


